export type Cluster = 'llm agents' | 'nlp research' | 'applied ml' | 'systems';

export interface Project {
  id: string;
  name: string;
  year: number;
  cluster: Cluster;
  summary: string;
  tags: string[];
  metrics: [string, string][];
  body: string[];
  arch: string[];
  links: [string, string][];
  logo?: string;
}

export interface Pub {
  title: string;
  authors: string[];
  venue: string;
  year: number;
  abstract: string;
  chart: boolean;
  pdf?: string;
  /** Link label in the research list: e.g. "paper" vs "source" */
  linkLabel?: string;
}

export const PROJECTS: Project[] = [
  {
    id: 'clippy-vision',
    name: 'clippy vision',
    year: 2025,
    cluster: 'llm agents',
    summary:
      'Open-source, fully local AI assistant that watches your work: zero data leaves the machine',
    tags: ['python', 'ollama', 'vision', 'sqlite', 'react agent', 'open source'],
    metrics: [
      ['37%', 'fewer inference calls'],
      ['23%', 'less vision load'],
      ['100%', 'on-device'],
      ['1,000+', 'events evaluated'],
    ],
    body: [
      'Clippy Vision eliminates the context problem by passively watching your work 24/7. A 3-stage classification pipeline (rule-based → statistical → multimodal LLM) decides when the model actually needs to look, cutting inference calls by 37% across 1,000+ real events.',
      'Perceptual hash deduplication with Union-Find lowered vision-processing workload by 23% across 900+ screenshots. Everything stays on the machine: no cloud, no telemetry of your screen.',
      'The hard part was not the model. It was knowing when not to call it.',
    ],
    arch: ['capture', 'classify', 'dedupe', 'embed', 'react agent', 'answer'],
    links: [['view source', 'https://github.com/protocorn/clippy-vision']],
    logo: '/clippy_vision_logo.png',
  },
  {
    id: 'launchway',
    name: 'launchway',
    year: 2025,
    cluster: 'llm agents',
    summary: 'AI-powered job application CLI: 14 min applications cut to under 2',
    tags: ['python', 'playwright', 'gemini', 'cli'],
    metrics: [
      ['<2 min', 'per application'],
      ['86%', 'field fill rate'],
      ['650+', 'applications'],
      ['12', 'beta users'],
    ],
    body: [
      'Launchway cuts average job application time from ~14 minutes to under 2. A 3-layer form-filling fallback (deterministic mapping → learned patterns → AI inference) achieved 86% field-population success across 650+ applications for 12 beta users.',
      'Telemetry across 41 run-level metrics made the failure modes visible: wrong selectors, ambiguous labels, and sites that change under you. The interesting behaviour only shows up once those stop being interesting.',
    ],
    arch: ['search', 'parse JD', 'tailor resume', 'fill form', 'fallback', 'submit'],
    links: [
      ['live site', 'https://www.launchway.app'],
      ['view source', 'https://github.com/protocorn/job_application_agent'],
    ],
    logo: '/launchway_logo.png',
  },
  {
    id: 'mimikree',
    name: 'mimikree',
    year: 2025,
    cluster: 'llm agents',
    summary: 'A personal LLM that learns you from your own data',
    tags: ['python', 'langchain', 'pinecone', 'flask', 'docker'],
    metrics: [
      ['55+', 'users'],
      ['450+', 'indexed docs'],
      ['hybrid', 'embeddings'],
      ['jwt', 'auth'],
    ],
    body: [
      'Mimikree builds a private assistant around a single person. It ingests notes, messages, documents, and browsing exports, then constructs a retrieval index that lets a general-purpose model answer as though it has known you for years.',
      'An adaptive RAG pipeline with query expansion and hybrid embeddings (70% expanded / 30% original) retrieves context from 450+ indexed documents across GitHub, social, and uploaded sources: backed by JWT auth and a rate-limited, containerized backend.',
      'Every answer carries its sources. If the model cannot ground a claim, it says so.',
    ],
    arch: ['ingest', 'chunk + embed', 'hybrid index', 'query expand', 'retrieve', 'grounded answer'],
    links: [
      ['live site', 'https://www.mimikree.com'],
      ['view source', 'https://github.com/protocorn/Mimikree---Your-own-LLM'],
    ],
    logo: '/Mimikree_logo.png',
  },
  {
    id: 'speculative-decoding',
    name: 'domain-aligned speculative decoding',
    year: 2025,
    cluster: 'nlp research',
    summary: 'QLoRA + DPO fine-tune, then speculative decoding measured end to end',
    tags: ['python', 'pytorch', 'qlora', 'dpo', 'phi-3'],
    metrics: [
      ['3.8B', 'phi-3 mini'],
      ['985', 'preference pairs'],
      ['qlora+dpo', 'alignment'],
      ['custom', 'decoding engine'],
    ],
    body: [
      'Fine-tuned Phi-3 Mini (3.8B) via QLoRA + DPO on 985 synthetic preference pairs, then built a speculative decoding engine from scratch using Phi-3 Medium as the verifier.',
      'The goal was measuring how domain alignment affects inference throughput: evaluating the speed-quality trade-off end to end, not just publishing a loss curve.',
    ],
    arch: ['preference pairs', 'qlora', 'dpo', 'draft model', 'verify', 'eval'],
    links: [['view source', 'https://github.com/protocorn/Research_DPO']],
  },
  {
    id: 'medical-infographics',
    name: 'medical infographics',
    year: 2024,
    cluster: 'applied ml',
    summary: 'Turns medical research papers into readable infographics',
    tags: ['python', 'django', 'nlp', 'transformers'],
    metrics: [
      ['auto', 'layout'],
      ['pdf → graphic', 'pipeline'],
      ['t5/bart', 'summarization'],
    ],
    body: [
      'Medical literature is written for reviewers, not for the clinicians and patients who need the finding. This pipeline segments a paper, summarizes key points, finds relevant images, and lays the result out as a readable graphic.',
      'Extraction is the fragile step: papers disagree about where anything lives. The system reads structure rather than position.',
    ],
    arch: ['pdf parse', 'section detect', 'summarize', 'image match', 'layout', 'render'],
    links: [
      [
        'view source',
        'https://github.com/protocorn/automatic-infographics-system-for-medical-paper',
      ],
    ],
  },
  {
    id: 'gmail-assistant',
    name: 'smart gmail assistant',
    year: 2024,
    cluster: 'systems',
    summary: 'Chrome extension for AI compose, tone, and summarization in Gmail',
    tags: ['javascript', 'chrome', 'oauth', 'chrome ai'],
    metrics: [
      ['chrome store', 'live'],
      ['on-device', 'chrome ai apis'],
      ['gmail', 'integration'],
    ],
    body: [
      'A Chrome extension that enhances Gmail with AI-powered composition, tone refinement, summarization, and smart management: without leaving the inbox.',
      'Built on Chrome AI APIs (Summarizer, Translator, Prompt) so inference can stay closer to the user when the browser supports it.',
    ],
    arch: ['oauth', 'read thread', 'prompt', 'compose', 'insert'],
    links: [
      [
        'chrome store',
        'https://chromewebstore.google.com/detail/smart-gmail-assistant/ieccgcgkaihnjkocfaobaamapolhjckb',
      ],
      [
        'view source',
        'https://github.com/protocorn/Smart-AI-Assistant---Chrome-Extension',
      ],
    ],
    logo: '/smart_assistant_logo.png',
  },
];

export const PUBS: Pub[] = [
  {
    title:
      'Cross-Domain Sentiment Analysis: An Extensive Study of Machine Learning and Deep Learning Models, Datasets, and Preprocessing Techniques for Predictive Performance',
    authors: ['Sahil Chordia', 'Jaya Gupta', 'Shubham Jain'],
    venue:
      'Congress on Smart Computing Technologies (CSCT 2023) · Springer · published Oct 2024 · SIST vol. 395, pp. 291-308',
    year: 2024,
    abstract:
      'Trained models on a generic Twitter dataset to predict sentiment on Amazon and IMDb reviews, with BiLSTM achieving the best cross-domain accuracy (82.56%, 80.43%, 79.48% across 3 datasets).',
    chart: true,
    pdf: 'https://link.springer.com/chapter/10.1007/978-981-97-5081-8_24',
    linkLabel: 'paper',
  },
  {
    title: 'Domain-Aligned Speculative Decoding: Speed-Quality Trade-offs under Preference Tuning',
    authors: ['Sahil Chordia'],
    venue: 'Individual research · Phi-3 Mini + Medium · QLoRA / DPO',
    year: 2025,
    abstract:
      'Work examining how domain alignment via QLoRA and direct preference optimization interacts with speculative decoding throughput, measuring the speed-quality trade-off when a draft model and verifier share domain-tuned preferences.',
    chart: false,
    pdf: 'https://github.com/protocorn/Research_DPO',
    linkLabel: 'source',
  },
];

export interface SkillGroup {
  level: string;
  blurb: string;
  items: string[];
}

/** Grouped like the resume: tags, not fake % bars */
export const SKILLS: SkillGroup[] = [
  {
    level: 'languages',
    blurb: 'day-to-day working languages',
    items: ['python', 'sql'],
  },
  {
    level: 'machine learning & ai',
    blurb: 'training, fine-tuning, and classical ML',
    items: ['pytorch', 'scikit-learn', 'hugging face', 'clip', 'fine-tuning', 'qlora', 'dpo'],
  },
  {
    level: 'llm & agent systems',
    blurb: 'retrieval, agents, and local inference',
    items: ['rag', 'langchain', 'langgraph', 'ollama', 'agentic workflows', 'prompt engineering'],
  },
  {
    level: 'data & vector systems',
    blurb: 'pipelines, stores, and embeddings',
    items: ['pandas', 'numpy', 'postgresql', 'redis', 'pinecone', 'qdrant', 'faiss'],
  },
  {
    level: 'backend & infrastructure',
    blurb: 'serving and shipping systems',
    items: ['fastapi', 'docker', 'aws'],
  },
];

export const TRAINING = [
  'MS Data Science, University of Maryland, College Park (2024-2026)',
  'B.E. Computer Engineering, A.P. Shah Institute of Technology (2020-2024)',
  'AI Intern: Raising the Floor: LLM audit pipeline, Gemma2 + Qdrant chatbot',
  'Teaching Assistant: Big Data Systems, UMD (Spark, distributed data)',
  'Peer-reviewed publication in cross-domain sentiment analysis (CSCT 2023)',
  'Open source: everything on this site is public on GitHub',
];

export interface ExperienceItem {
  id: string;
  title: string;
  org: string;
  location: string;
  dates: string;
  type: string;
  bullets: string[];
  metrics?: [string, string][];
  tags?: string[];
  logo?: string;
}

export const EXPERIENCE: ExperienceItem[] = [
  {
    id: 'rtf',
    title: 'AI Intern',
    org: 'Raising the Floor',
    location: 'Washington, D.C. (Remote)',
    dates: 'Jun 2025 - Present',
    type: 'internship',
    logo: '/raising_the_floor_logo.png',
    metrics: [
      ['96%', 'audit time cut'],
      ['60', 'tools / month'],
      ['360+', 'AT tools audited'],
    ],
    bullets: [
      'Built an LLM pipeline that auto-flags metadata errors across assistive technology catalogs, cutting audit time by 96%.',
      'Shipped a Gemma2 + Qdrant chatbot for tool recommendation under ambiguous user queries.',
      'Audited 360+ assistive tech tools at ~60 tools / month for catalog quality and accessibility metadata.',
    ],
    tags: ['llm', 'rag', 'gemma2', 'qdrant', 'assistive tech'],
  },
  {
    id: 'ta-umd',
    title: 'Teaching Assistant: Big Data Systems',
    org: 'University of Maryland, College Park',
    location: 'College Park, MD',
    dates: '2025 - Present',
    type: 'academic',
    logo: '/Univ_Maryland_logo.png',
    metrics: [
      ['60', 'grad students'],
      ['spark', 'distributed data'],
    ],
    bullets: [
      'Support graduate coursework on Spark and distributed data systems.',
      'Hold office hours, grade assignments, and help students debug large-scale data pipelines.',
    ],
    tags: ['spark', 'distributed systems', 'teaching'],
  },
];

export interface EducationItem {
  id: string;
  degree: string;
  school: string;
  location: string;
  dates: string;
  detail?: string;
  highlights?: string[];
  logo?: string;
}

export const EDUCATION: EducationItem[] = [
  {
    id: 'ms',
    degree: 'MS Data Science',
    school: 'University of Maryland, College Park',
    location: 'College Park, MD',
    dates: '2024 - 2026',
    logo: '/Univ_Maryland_logo.png',
    detail: 'Focus on ML, statistical modeling, and distributed data systems.',
    highlights: [
      'Teaching Assistant: Big Data Systems (Spark, distributed data)',
      'Coursework spanning retrieval systems, statistical learning, and production ML',
    ],
  },
  {
    id: 'be',
    degree: 'B.E. Computer Engineering',
    school: 'A.P. Shah Institute of Technology',
    location: 'Mumbai, India',
    dates: '2020 - 2024',
    logo: '/apsit_logo.jpg',
    detail: 'Graduated with distinction.',
    highlights: [
      'Peer-reviewed publication in cross-domain sentiment analysis (CSCT 2023 · Springer)',
      'Built end-to-end ML and full-stack projects shipped publicly on GitHub',
    ],
  },
  {
    id: 'pace',
    degree: 'Science (PCM)',
    school: 'Pace Junior Science College',
    location: 'Mumbai, India',
    dates: '2018 - 2020',
  },
];

export const EVALS: [string, string][] = [
  ['papers published', '1'],
  ['projects shipped end-to-end', '6+'],
  ['production / live products', '3'],
  ['assistive tools audited / month', '60'],
  ['languages held to a professional standard', '4'],
];

export const LIMITATIONS = [
  'Overthinks problems that a for-loop would have solved',
  'Ships slowly when the abstraction is not right yet',
  'Still building depth in computer vision',
  'Caffeine-dependent build cycles',
];

export const CHANGELOG: [string, string, string][] = [
  ['v2.5', '2025-06', 'ai intern at raising the floor: 96% audit time cut'],
  ['v2.4', '2025-05', 'clippy vision: local multimodal assistant'],
  ['v2.3', '2025-04', 'launchway beta: 86% form fill across 650+ apps'],
  ['v2.2', '2024-08', 'started ms data science at umd'],
  ['v2.1', '2025-01', 'mimikree live with hybrid rag'],
  ['v2.0', '2023-09', 'first paper published: csct 2023'],
];

export const CONTACT = {
  email: 'chordiasahil@gmail.com',
  github: 'https://github.com/protocorn',
  linkedin: 'https://linkedin.com/in/sahil--chordia',
  resume: '/resume.pdf',
  location: 'College Park, MD',
  photo: '/linkedin_headshot.jpg',
};

export interface BlogPost {
  id: string;
  title: string;
  summary: string;
  date: string;
  read: string;
  url: string;
  tags: string[];
  isNew?: boolean;
}

export const BLOGS: BlogPost[] = [
  {
    id: 'google-vs-chatgpt',
    title: 'Google vs. ChatGPT: Why One Gives You the Bomb and the Other Refuses',
    summary:
      'Search engines index the messy internet; chatbots synthesize and filter it. On the safety tax, deep ignorance, and what we lose when AI navigates for us.',
    date: '2025-12-04',
    read: '9 min',
    url: 'https://medium.com/@chordiasahil24/google-vs-chatgpt-why-one-gives-you-the-bomb-and-the-other-refuses-19377a32d533',
    tags: ['ai safety', 'ethics', 'search'],
    isNew: true,
  },
  {
    id: 'digital-canvas',
    title: "The Digital Canvas: A Beginner's Guide to How AI Generates Images",
    summary:
      'From text prompts to pixels: how generative image models turn language into visuals, and why that changes how we see creative work.',
    date: '2025-07-30',
    read: '22 min',
    url: 'https://medium.com/@chordiasahil24/the-digital-canvas-a-beginners-guide-to-how-ai-generates-images-2133e6d5bdea',
    tags: ['genai', 'stable diffusion', 'ml'],
  },
  {
    id: 'mimikree-medium',
    title: 'Mimikree: Building Personalized Shareable LLMs with Node.js and Gemini 2.0 Flash',
    summary:
      'How Mimikree builds a digital twin from your data: RAG over Pinecone, Gemini Flash 2.0, and shareable model links.',
    date: '2025-03-19',
    read: '8 min',
    url: 'https://medium.com/@chordiasahil24/building-personalized-shareable-llms-using-node-js-and-gemini-2-0-flash-api-4e3bb127190e',
    tags: ['llm', 'rag', 'gemini'],
  },
];

/** Home “now” briefing: experience & status, not projects */
export const NOW = {
  role: {
    title: 'AI Intern',
    org: 'Raising the Floor',
    detail: 'Assistive technology nonprofit · Washington, D.C. (Remote)',
    dates: 'Jun 2025 - Present',
    metrics: [
      ['96%', 'audit time cut'],
      ['60', 'tools / month'],
      ['360+', 'AT tools audited'],
    ] as [string, string][],
    lines: [
      'LLM pipeline that auto-flags metadata errors across assistive tech catalogs.',
      'Gemma2 + Qdrant chatbot for tool recommendation under ambiguous queries.',
    ],
  },
  education: {
    degree: 'MS Data Science',
    school: 'University of Maryland, College Park',
    dates: '2024 - 2026',
    side: 'TA: Big Data Systems (Spark, distributed data)',
  },
  seeking: ['AI / ML engineer roles', 'RAG & agent systems', 'production LLM infra'],
  focus: ['grounded generation', 'retrieval quality', 'latency budgets'],
};
